// apps/frontend/src/api/email.ts
import api from './client'

export interface InboxParams {
  folder?: 'inbox' | 'sent' | 'drafts' | 'scheduled' | 'trash'
  accountId?: string
  search?: string
  page?: number
  limit?: number
}

export interface SendEmailPayload {
  toAddresses: string[]
  ccAddresses?: string[]
  bccAddresses?: string[]
  subject: string
  bodyHtml?: string
  bodyText?: string
  accountId?: string
  threadId?: string
  sendAt?: string
  refId?: string
  refType?: 'lead' | 'deal'
}

export interface EmailMessage {
  id: string
  companyId: string
  refType: 'lead' | 'deal' | null
  refId: string | null
  direction: 'sent' | 'received'
  fromAddress: string
  toAddresses: string[]
  ccAddresses: string[]
  bccAddresses: string[]
  subject: string
  bodyHtml: string | null
  bodyText: string | null
  attachments: Array<{ filename: string; size: number; mimeType: string }> | null
  messageId: string | null
  threadId: string | null
  status: 'sent' | 'delivered' | 'failed' | 'received' | 'scheduled' | 'draft'
  isRead: boolean
  sentBy: string | null
  createdAt: string
  deletedAt: string | null
}

export interface PaginatedMessages {
  data: EmailMessage[]
  meta: { page: number; limit: number; total: number; totalPages: number }
}

function buildFormData(dto: SendEmailPayload, files: File[]): FormData {
  const fd = new FormData()
  // Append scalar/array fields individually so the server can parse them
  const appendArray = (key: string, arr: string[] | undefined) => {
    if (arr) arr.forEach(v => fd.append(key, v))
  }
  appendArray('toAddresses', dto.toAddresses)
  appendArray('ccAddresses', dto.ccAddresses)
  appendArray('bccAddresses', dto.bccAddresses)
  if (dto.subject) fd.append('subject', dto.subject)
  if (dto.bodyHtml) fd.append('bodyHtml', dto.bodyHtml)
  if (dto.bodyText) fd.append('bodyText', dto.bodyText)
  if (dto.accountId) fd.append('accountId', dto.accountId)
  if (dto.threadId) fd.append('threadId', dto.threadId)
  if (dto.sendAt) fd.append('sendAt', dto.sendAt)
  if (dto.refId) fd.append('refId', dto.refId)
  if (dto.refType) fd.append('refType', dto.refType)
  files.forEach(f => fd.append('attachments', f, f.name))
  return fd
}

export const emailApi = {
  inbox: (companyId: string, params: InboxParams = {}) =>
    api.get<PaginatedMessages>(`/companies/${companyId}/emails`, { params }),

  send: (companyId: string, refType: string, refId: string, dto: SendEmailPayload, files?: File[]) => {
    if (files && files.length > 0) {
      const fd = buildFormData(dto, files)
      return api.post<EmailMessage>(`/companies/${companyId}/${refType}s/${refId}/emails/send`, fd)
    }
    return api.post<EmailMessage>(`/companies/${companyId}/${refType}s/${refId}/emails/send`, dto)
  },

  sendUnlinked: (companyId: string, dto: SendEmailPayload, files?: File[]) => {
    if (files && files.length > 0) {
      const fd = buildFormData(dto, files)
      return api.post<EmailMessage>(`/companies/${companyId}/emails/send`, fd)
    }
    return api.post<EmailMessage>(`/companies/${companyId}/emails/send`, dto)
  },

  history: (companyId: string, refType: string, refId: string, page = 1, limit = 50) =>
    api.get<PaginatedMessages>(`/companies/${companyId}/${refType}s/${refId}/emails`, { params: { page, limit } }),

  cancelScheduled: (companyId: string, messageId: string) =>
    api.delete(`/companies/${companyId}/emails/${messageId}/scheduled`),

  markRead: (companyId: string, id: string) =>
    api.patch(`/companies/${companyId}/emails/${id}/read`),

  getUnreadCount: (companyId: string) =>
    api.get<{ count: number }>(`/companies/${companyId}/emails/unread-count`),

  getThread: (companyId: string, threadId: string) =>
    api.get<EmailMessage[]>(`/companies/${companyId}/emails/thread`, { params: { threadId } }),

  updateRef: (companyId: string, id: string, refId: string | null, refType: 'lead' | 'deal' | null) =>
    api.patch(`/companies/${companyId}/emails/${id}/ref`, { refId, refType }),

  saveDraft: (companyId: string, dto: Partial<SendEmailPayload>, draftId?: string) =>
    api.post<EmailMessage>(`/companies/${companyId}/emails/draft${draftId ? `?draftId=${encodeURIComponent(draftId)}` : ''}`, dto),

  updateDraft: (companyId: string, id: string, dto: Partial<SendEmailPayload>) =>
    api.patch<EmailMessage>(`/companies/${companyId}/emails/${id}/draft`, dto),

  deleteDraft: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/emails/${id}/draft`),

  downloadAttachment: (companyId: string, messageId: string, index: number) =>
    api.get<Blob>(`/companies/${companyId}/emails/${messageId}/attachments/${index}/download`, { responseType: 'blob' }),
}
